<template>
  <div class="draft-invoice-line-adjustments">
    <div v-for="adj in visibleAdjustments" :key="adj.id" class="adjustment-row d-flex align-items-start flex-wrap gap-2 py-1 px-1">
      <div style="width: 250px; flex-shrink: 0;">
        <wiskSelect
          :modelValue="adj._typeItem"
          :items="allAdjustmentTypes"
          :label="translations.txtAdjustmentTypeSelectorLabel"
          trackBy="id"
          disabled
        />
      </div>

      <div style="width: 140px; flex-shrink: 0;">
        <wiskInput
          :modelValue="adj.rate"
          :prefix="adj._ratePrefix"
          :suffix="adj._rateSuffix"
          inputType="number" :decimals="4" decimalsAsNeeded :minDecimals="2"
          :disabled="disabled"
          @operation="onRateChange(adj, $event)"
          operation="rate"
          :label="translations.txtAdjustmentRate"
          class="mb-0"
        />
        <div v-if="adj._basisLabel" class="text-gray-400">{{ adj._basisLabel }}</div>
      </div>

      <div style="width: 120px; flex-shrink: 0;">
        <wiskInput
          :modelValue="getComputedTotal(adj)"
          :prefix="currency"
          inputType="number" :decimals="2" :minDecimals="2"
          disabled
          :label="translations.txtGenericTotal"
        />
      </div>

      <div class="d-flex align-items-center gap-2 flex-shrink-0">
        <AdjustmentTypeCapitalizeBadge :capitalizeIntoCost="adj.capitalize_into_cost" />

        <b-button v-if="!disabled" size="sm" variant="link" @click="deleteAdjustment(adj)"
          v-tooltip="translations.txtGenericRemove">
          <icon name="wisk-trash" :scale="0.9" class="text-danger" />
        </b-button>
      </div>
    </div>

    <div v-if="showAddForm" class="d-flex align-items-center gap-2 py-2">
      <div style="min-width: 180px; max-width: 250px; flex: 1 1 180px; position: relative;">
        <wiskSelect
          :items="activeAdjustmentTypes"
          :modelValue="null"
          @change="onNewTypeSelected"
          :label="translations.txtAdjustmentTypeSelectorLabel"
          :addNewItem="addAdjustmentTypeConfig"
          trackBy="id"
          fixedWrapper
          :disabled="disabled"
        />
        <b-button class="text-primary" style="position: absolute; top: -25px; right: -5px;" variant="link" v-tooltip="translations.txtAdjustmentTypes"
          @click="setGlobalAction({ type: 'adjustmentTypes', action: {} })">
          <icon name="wisk-options" :scale="0.9" />
        </b-button>
      </div>
      <b-button size="sm" variant="link" @click="showAddForm = false" v-tooltip="translations.txtGenericCancel" class="flex-shrink-0 align-self-start mt-1">
        <icon name="wisk-trash" :scale="0.9" class="text-danger" />
      </b-button>
    </div>

    <b-button v-if="!disabled && !showAddForm" size="sm" variant="link" @click="showAddForm = true" class="mt-1 ps-0 text-primary">
      <icon name="wisk-plus" :scale="0.7" class="me-1" />
      {{ translations.txtAdjustmentAddAdjustment }}
    </b-button>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { currencyFormat } from '@/modules/utils'
import AdjustmentTypeCapitalizeBadge from '@/components/accounting/AdjustmentTypeCapitalizeBadge'

export default {
  name: 'DraftInvoiceLineAdjustments',
  components: { AdjustmentTypeCapitalizeBadge },
  emits: ['pendingChanged'],
  props: {
    lineItem: Object,
    disabled: Boolean,
    saving: Boolean,
    calculatedAdjustments: { type: Array, default: () => [] }
  },
  data() {
    return {
      localAdjustments: [],
      showAddForm: false,
      nextTempId: -1
    }
  },
  computed: {
    ...mapState(['translations', 'adjustmentTypes', 'adjustmentTypesById']),
    ...mapGetters(['currency']),
    allAdjustmentTypes() {
      return this.adjustmentTypes.map(t => ({ ...t, title: t.label }))
    },
    activeAdjustmentTypes() {
      return this.allAdjustmentTypes.filter(t => !t.archived)
    },
    basisLabels() {
      return {
        per_unit: this.translations.txtAdjustmentTypeBasisPerUnit,
        per_case: this.translations.txtAdjustmentTypeBasisPerCase,
        percent_of_line: this.translations.txtAdjustmentTypeBasisPercentOfLine
      }
    },
    visibleAdjustments() {
      return this.localAdjustments.filter(a => !a._deleted).map(adj => {
        const adjType = this.adjustmentTypesById[adj.adjustment_type_id]
        const isPercent = adjType?.basis === 'percent_of_line'
        return {
          ...adj,
          _typeItem: this.allAdjustmentTypes.find(t => t.id === adj.adjustment_type_id) ?? null,
          _ratePrefix: isPercent ? '' : this.currency,
          _rateSuffix: isPercent ? '%' : '',
          _basisLabel: this.basisLabels[adjType?.basis] ?? null
        }
      })
    },
    addAdjustmentTypeConfig() {
      return {
        action: (id, searchQuery, value, callbackItemInjected) => {
          this.setGlobalAction({
            type: 'adjustmentTypeEdit',
            action: {
              id: 0,
              onChange: type => {
                callbackItemInjected(type)
              }
            }
          })
        },
        label: this.translations.txtAdjustmentTypeNew
      }
    }
  },
  methods: {
    currencyFormat,
    ...mapActions(['setGlobalAction']),
    getComputedTotal(adj) {
      if (adj._new) {
        const newVisibleIndex = this.visibleAdjustments.filter(a => a._new).findIndex(a => a.id === adj.id)
        const newCalcAdjs = (this.calculatedAdjustments || []).filter(a => a.id === 0)
        return newCalcAdjs[newVisibleIndex]?.computed_total ?? adj.computed_total
      }
      const calcAdj = (this.calculatedAdjustments || []).find(a => a.id === adj.id)
      return calcAdj?.computed_total ?? adj.computed_total
    },
    onRateChange(adj, operation) {
      const adjIndex = this.localAdjustments.findIndex(a => a.id === adj.id)
      if (adjIndex >= 0) {
        this.localAdjustments[adjIndex].rate = operation.value
      }
      this.emitPendingChanged()
    },
    deleteAdjustment(adj) {
      const adjIndex = this.localAdjustments.findIndex(a => a.id === adj.id)
      if (adjIndex >= 0) {
        if (this.localAdjustments[adjIndex]._new) {
          this.localAdjustments.splice(adjIndex, 1)
        } else {
          this.localAdjustments[adjIndex]._deleted = true
        }
      }
      this.emitPendingChanged()
    },
    onNewTypeSelected(type) {
      if (!type) return
      this.showAddForm = false
      const adjType = this.adjustmentTypesById[type.id]
      this.localAdjustments.push({
        id: this.nextTempId--,
        adjustment_type_id: type.id,
        rate: type.default_rate || 0,
        computed_total: 0,
        capitalize_into_cost: adjType?.capitalize_into_cost || false,
        _new: true,
        _deleted: false,
        _savedRate: null
      })
      this.emitPendingChanged()
    },
    pendingOperations() {
      const result = []
      for (const adj of this.localAdjustments) {
        if (adj._new && !adj._deleted) {
          result.push({ id: null, ops: [{ type: 'adjustment_type_id', value: adj.adjustment_type_id }, { type: 'rate', value: adj.rate }] })
        } else if (!adj._new && adj._deleted) {
          result.push({ id: adj.id, ops: [{ type: 'delete' }] })
        } else if (!adj._new && !adj._deleted && adj.rate !== adj._savedRate) {
          result.push({ id: adj.id, ops: [{ type: 'rate', value: adj.rate }] })
        }
      }
      return result
    },
    emitPendingChanged() {
      const adjustments = this.pendingOperations().map(({ id, ops }) => ({ id, operations: ops }))
      this.$emit('pendingChanged', adjustments)
    }
  },
  watch: {
    'lineItem.adjustments': {
      immediate: true,
      handler(adjustments) {
        if (this.saving) return
        this.localAdjustments = (adjustments || []).map(a => ({
          ...a,
          _new: false,
          _deleted: false,
          _savedRate: a.rate
        }))
        this.emitPendingChanged()
      }
    }
  }
}
</script>
